/**
 * The pattern name the texture generator knows, from however a person typed it
 * (a CSV "layout" column: "Fish Scale", "penny-round", "Hexagon ", "Brick bond",
 * "écaille", "crazy paving"). Unknown names come back lowercased and without
 * separators, never guessed, so the generator reports them instead of a wrong
 * pattern being laid.
 */
const ALIASES: Record<string, string> = {
  // Plank and grid patterns. 'standard', 'stacked' and 'brick-bond' are what
  // older families list on their pattern axis (ARDE-293).
  standard: 'stackbond',
  straight: 'stackbond',
  straightlay: 'stackbond',
  grid: 'stackbond',
  checkerboard: 'stackbond',
  damier: 'stackbond',
  droit: 'stackbond',
  posedroite: 'stackbond',
  decale: 'bricks',
  posedecalee: 'bricks',
  herring: 'herringbone',
  batonsrompus: 'herringbone',
  espiga: 'herringbone',
  spinadipesce: 'herringbone',
  chevrons: 'chevron',
  pointdehongrie: 'chevron',
  hexagons: 'hexagonal',
  hexagone: 'hexagonal',
  tomette: 'hexagonal',
  tomettes: 'hexagonal',
  romanopus: 'opus',
  fan: 'fishscale',
  mermaid: 'fishscale',
  ecailles: 'fishscale',
  roundmosaic: 'penny',
  pastille: 'penny',
  pastilles: 'penny',
  onda: 'wave',
  brick: 'bricks',
  brickbond: 'bricks',
  runningbond: 'bricks',
  stack: 'stackbond',
  stacked: 'stackbond',
  stackbond2x2: 'stackbond-2x2', // the generator's name keeps its hyphen
  fishbone: 'herringbone',
  hexa: 'hexagonal',
  hex: 'hexagonal',
  hexagon: 'hexagonal',
  opusromano: 'opus',
  // Shaped tiles
  scale: 'fishscale',
  scales: 'fishscale',
  fishscales: 'fishscale',
  scallop: 'fishscale',
  ecaille: 'fishscale',
  lantern: 'arabesque',
  moroccanlantern: 'arabesque',
  alhambra: 'arabesque',
  arabesco: 'arabesque',
  arrows: 'arrow',
  arrowtile: 'arrow',
  picket: 'arrow',
  pickets: 'arrow',
  pennyround: 'penny',
  pennyrounds: 'penny',
  pennies: 'penny',
  triangles: 'triangle',
  triangolo: 'triangle',
  waves: 'wave',
  wavetile: 'wave',
  wowwave: 'wave',
  vague: 'wave',
  longhexagon: 'longhex',
  longhexagonal: 'longhex',
  elongatedhexagon: 'longhex',
  hexagoneallonge: 'longhex',
  // Narrow + square rows
  alternate: 'alternating',
  alterne: 'alternating',
  // Random stone
  flagstones: 'flagstone',
  randomstone: 'flagstone',
  irregularstone: 'flagstone',
  crazypaving: 'flagstone',
  opusincertum: 'flagstone',
  tightflagstone: 'flagstonetight',
  flagstonestight: 'flagstonetight',
  crazypavingtight: 'flagstonetight',
};

export function normalizePatternName(raw: unknown): string | undefined {
  if (raw === undefined || raw === null) return undefined;
  const key = String(raw)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // é → e
    .replace(/[\s_-]+/g, '');
  if (!key) return undefined;
  return ALIASES[key] ?? key;
}

/** The patterns the texture generator lays (the pattern grid's tiles, plus stack bond 2×2). */
export const KNOWN_PATTERNS: ReadonlySet<string> = new Set([
  'stackbond', 'stackbond-2x2', 'bricks', 'herringbone', 'chevron', 'opus', 'cabochon', 'hexagonal',
  'fishscale', 'arabesque', 'alure', 'arrow', 'penny', 'triangle', 'flagstone', 'flagstonetight',
  'wave', 'longhex', 'alternating',
]);

/**
 * The generator's name for a pattern, only when it is one ("Brick bond" →
 * 'bricks', "stacked" → 'stackbond'); undefined for anything it does not lay.
 */
export function knownPatternName(raw: unknown): string | undefined {
  const name = normalizePatternName(raw);
  return name && KNOWN_PATTERNS.has(name) ? name : undefined;
}

// ─────────────────────────────────────────────────────────────────────────────
// Layout options: words after the pattern in a CSV "layout" cell
// ─────────────────────────────────────────────────────────────────────────────

/** What the words set on the product's seamlessParams, as the generator reads them. */
export interface LayoutOptions {
  /** Stack bond: every other tile mirrored (2×2 blocks). */
  mirror?: boolean;
  /** Stack bond: false lays the photos in upload order instead of shuffling them. */
  randomize?: boolean;
  /** Bricks: how far each row is shifted. */
  offset?: 'random' | 'half' | 'third';
  /** Hexagons and shaped tiles: the layout turned the other way. */
  flip?: boolean;
}

type LayoutWord = 'mirror' | 'ordered' | 'half' | 'third' | 'random' | 'flip';

const WORD_EFFECT: Record<LayoutWord, LayoutOptions> = {
  mirror: { mirror: true },
  ordered: { randomize: false },
  half: { offset: 'half' },
  third: { offset: 'third' },
  random: { offset: 'random' },
  flip: { flip: true },
};

const WORD_ALIASES: Record<string, LayoutWord> = {
  mirrored: 'mirror',
  inorder: 'ordered',
  order: 'ordered',
  '1/2': 'half',
  '1/3': 'third',
  flipped: 'flip',
};

/**
 * The words each pattern accepts after its name ("stackbond:mirror",
 * "bricks:third", "hexagonal:flip"). A new pattern option is one more word here
 * (and in WORD_EFFECT); the CSV format stays the same.
 */
export const LAYOUT_WORDS: Record<string, LayoutWord[]> = {
  stackbond: ['mirror', 'ordered'],
  'stackbond-2x2': ['ordered'],
  bricks: ['half', 'third', 'random'],
  hexagonal: ['flip'],
  fishscale: ['flip'],
  arabesque: ['flip'],
  alure: ['flip'],
  arrow: ['flip'],
  triangle: ['flip'],
  wave: ['flip'],
  longhex: ['flip'],
};

export interface ParsedLayout {
  pattern?: string;
  options: LayoutOptions;
  /** Words this pattern does not know, as typed. */
  unknownWords: string[];
}

function tidyWord(raw: string): string {
  return raw.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[\s_-]+/g, '');
}

/**
 * A CSV layout cell: the pattern, then optional words each after a colon
 * ("stackbond:mirror:ordered"). A cell without words is just the pattern, as
 * before; words the pattern does not accept are reported, never guessed.
 */
export function parseLayout(raw: unknown): ParsedLayout {
  if (raw === undefined || raw === null || String(raw).trim() === '') {
    return { pattern: undefined, options: {}, unknownWords: [] };
  }
  const [head, ...words] = String(raw).split(':');
  const pattern = normalizePatternName(head);
  const allowed = (pattern && LAYOUT_WORDS[pattern]) || [];
  const options: LayoutOptions = {};
  const unknownWords: string[] = [];
  for (const typed of words) {
    if (!typed.trim()) continue;
    const key = tidyWord(typed);
    const word = (WORD_ALIASES[key] ?? key) as LayoutWord;
    if (allowed.includes(word)) Object.assign(options, WORD_EFFECT[word]);
    else unknownWords.push(typed.trim());
  }
  return { pattern, options, unknownWords };
}

/** The layout options set in a seamlessParams object, and only those. */
export function pickLayoutOptions(params: Partial<LayoutOptions> | undefined | null): LayoutOptions {
  const out: LayoutOptions = {};
  if (!params) return out;
  if (typeof params.mirror === 'boolean') out.mirror = params.mirror;
  if (typeof params.randomize === 'boolean') out.randomize = params.randomize;
  if (params.offset === 'random' || params.offset === 'half' || params.offset === 'third') out.offset = params.offset;
  if (typeof params.flip === 'boolean') out.flip = params.flip;
  return out;
}
